import { Link, useLocation } from "react-router";
import { ChevronRight, Home } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const ROUTE_LABELS: Record<string, string> = {
    app: "Dashboard",
    dashboard: "Dashboard",
    calendar: "Kalender",
    patients: "Patienten",
    inbox: "Postfach",
    billing: "Abrechnung",
    tickets: "Tickets",
    "ai-phone": "KI-Telefon",
    settings: "Einstellungen",
};

interface BreadcrumbProps {
    className?: string;
    /** Override automatic path detection with custom crumbs */
    items?: Array<{ label: string; href?: string }>;
}

export function Breadcrumb({ className, items }: BreadcrumbProps) {
    const location = useLocation();

    const crumbs = items || generateCrumbs(location.pathname);

    if (crumbs.length <= 1) return null;

    return (
        <nav aria-label="Breadcrumb" className={cn("flex items-center gap-1 text-sm", className)}>
            <Link
                to="/app/dashboard"
                className="flex items-center text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
                aria-label="Dashboard"
            >
                <Home className="h-3.5 w-3.5" />
            </Link>

            {crumbs.slice(1).map((crumb, i) => {
                const isLast = i === crumbs.length - 2;
                return (
                    <motion.div
                        key={crumb.label}
                        className="flex items-center gap-1"
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: i * 0.05 }}
                    >
                        <ChevronRight className="h-3 w-3 text-neutral-300 dark:text-neutral-600" />
                        {isLast || !crumb.href ? (
                            <span className="font-medium text-neutral-700 dark:text-neutral-200">
                                {crumb.label}
                            </span>
                        ) : (
                            <Link
                                to={crumb.href}
                                className="text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
                            >
                                {crumb.label}
                            </Link>
                        )}
                    </motion.div>
                );
            })}
        </nav>
    );
}

function generateCrumbs(pathname: string): Array<{ label: string; href?: string }> {
    const segments = pathname.split("/").filter(Boolean);
    const crumbs: Array<{ label: string; href?: string }> = [];

    let path = "";
    for (const segment of segments) {
        path += `/${segment}`;
        const label = ROUTE_LABELS[segment] || decodeURIComponent(segment);
        crumbs.push({ label, href: path });
    }

    return crumbs;
}
